import type { ItemId } from '@/data/items';
import type { NpcId } from '@/data/npcs';

/**
 * What the neighbours are asking for.
 *
 * Small, daily, and always for something you would have gathered anyway: a
 * request is a reason to go somewhere, not a chore bolted onto the farm. Three
 * go up each morning and come down at dusk, which is what keeps the board
 * worth walking past rather than a list that waits forever.
 *
 * Pay is authored here rather than derived from `sellPrice`, because most of
 * what gets asked for — fiber, driftwood, shells — is worth nothing at the
 * cart. What a favour is worth is not what the goods are worth.
 */

export interface RequestTemplate {
  readonly id: string;
  /** Who pinned it up. Filling it is a kindness they remember. */
  readonly from: NpcId;
  readonly item: ItemId;
  /** The count is rolled between these, so the same request is not the same twice. */
  readonly least: number;
  readonly most: number;
  /** Coins per item handed over. */
  readonly payPer: number;
  readonly note: string;
}

/** How many go up each morning. */
export const REQUESTS_PER_DAY = 3;

/** What filling one is worth to whoever asked. */
export const REQUEST_FRIENDSHIP = 12;

/**
 * The pool.
 *
 * Deliberately wider than three so the board differs day to day, and spread
 * across the things there are to do: the plot, the water, the sand, and what
 * lives underground.
 */
export const REQUEST_TEMPLATES: readonly RequestTemplate[] = [
  {
    id: 'pell-fiber',
    from: 'pell',
    item: 'mat:fiber',
    least: 6,
    most: 12,
    payPer: 8,
    note: 'Cord for the cart’s canopy. It frays in every wind.',
  },
  {
    id: 'pell-driftwood',
    from: 'pell',
    item: 'mat:driftwood',
    least: 4,
    most: 9,
    payPer: 14,
    note: 'Crate slats. The sea seasons wood better than I could.',
  },
  {
    id: 'pell-shell',
    from: 'pell',
    item: 'mat:shell',
    least: 5,
    most: 10,
    payPer: 12,
    note: 'Folk inland pay silly money for these. Do not tell them.',
  },
  {
    id: 'pell-feather',
    from: 'pell',
    item: 'mat:feather',
    least: 3,
    most: 7,
    payPer: 16,
    note: 'For a cushion. The road is hard on a man of my years.',
  },
  {
    id: 'pell-turnip',
    from: 'pell',
    item: 'crop:turnip',
    least: 4,
    most: 8,
    payPer: 26,
    note: 'Supper, and I am not proud about it.',
  },
  {
    id: 'pell-potato',
    from: 'pell',
    item: 'crop:potato',
    least: 3,
    most: 6,
    payPer: 34,
    note: 'They keep. Half of what I carry does not.',
  },
  {
    id: 'pell-egg',
    from: 'pell',
    item: 'egg',
    least: 3,
    most: 6,
    payPer: 30,
    note: 'Fresh ones. I can tell, so do not try me.',
  },
  {
    id: 'pell-blightSap',
    from: 'pell',
    item: 'mat:blightSap',
    least: 2,
    most: 5,
    payPer: 45,
    note: 'A smith downriver wants it. Do not ask me what for.',
  },
  {
    id: 'pell-mothDust',
    from: 'pell',
    item: 'mat:mothDust',
    least: 2,
    most: 4,
    payPer: 60,
    note: 'It glows. That is enough to sell it, in my experience.',
  },

  // Hers are all fuel and stock for the forge, which is what a smith's day is
  // actually made of.
  {
    id: 'mira-driftwood',
    from: 'mira',
    item: 'mat:driftwood',
    least: 6,
    most: 12,
    payPer: 15,
    note: 'Charcoal. Sea-dried burns hotter, and I cannot carry it all.',
  },
  {
    id: 'mira-blightSap',
    from: 'mira',
    item: 'mat:blightSap',
    least: 2,
    most: 4,
    payPer: 50,
    note: 'It quenches strangely. I want to know how strangely.',
  },
  {
    id: 'mira-shell',
    from: 'mira',
    item: 'mat:shell',
    least: 6,
    most: 12,
    payPer: 13,
    note: 'Ground down, they polish an edge better than any stone.',
  },
];
